import omit from "lodash/omit";
import {
  type AiFieldProposalFormValue,
  aiFieldProposalConfig,
} from "src/components/formFields/ai/utils/aiFieldProposalForm";
import { formatToPrice } from "src/components/Price";
import {
  AiFieldProposalStatus,
  type InputMaybe,
  type MaterialType,
  type MaterialVariant,
  type ResolveAiFieldProposalInput,
  type SaveMaterialListingVariantInput,
  type SaveMaterialVariantAssemblyComponentInput,
  type SaveMaterialVariantImageInput,
  type Scalars,
} from "src/generated/graphqlTypes";
import { type PotentialOperationDetailsFragment } from "src/queries/fragments.generated";
import {
  MaterialLeadTimeFormValue,
  OptionPricingFormValue,
  materialLeadTimeConfigConfig,
  optionPricingConfigConfig,
} from "src/routes/admin/utils";
import { isDefined, maybeTagFromId } from "src/utils";
import { ObjectConfig, ObjectState, required } from "src/utils/formState";

/**
 * Form shape for a MaterialListing and its variants, plus the helpers that map it to/from GraphQL.
 *
 * Kept free of React so the listing detail page (via `MaterialListingContext`) and the "Add Material"
 * page (via `MaterialListingForm`) can share one form definition without importing each other.
 */

export type VariantImageAsset = SaveMaterialVariantImageInput & {
  downloadUrl?: string;
  attachmentUrl?: string;
  /**
   * Where an imported photo was downloaded from. Matching it to the `imageUrl` proposal so we can
   * tell the AI's photo apart from the original.
   */
  sourceUrl?: string | null;
};

/** FE-only key so new (unsaved) assembly rows have a stable React key. */
export type AssemblyComponentFormValue = SaveMaterialVariantAssemblyComponentInput & { clientKey?: string };

export type MaterialVariantFormValue = {
  id?: InputMaybe<string>;
  code?: InputMaybe<string>;
  canEdit?: InputMaybe<PotentialOperationDetailsFragment>;
  modelNumber?: InputMaybe<string>;
  manufacturerUrl?: InputMaybe<string>;
  codeOverride?: InputMaybe<string>;
  mavIds?: InputMaybe<Array<string>>;
  tags?: InputMaybe<Array<string>>;
  images?: InputMaybe<Array<VariantImageAsset>>;
  components?: InputMaybe<Array<AssemblyComponentFormValue>>;
  deletedAt?: InputMaybe<Scalars["DateTime"]["input"]>;
  designFeedback?: InputMaybe<string>;
  priceEstimate?: InputMaybe<string>;
  fieldProposals?: InputMaybe<Array<AiFieldProposalFormValue>>;
};

export type MaterialListingFormValue = {
  id?: InputMaybe<string>;
  name?: InputMaybe<string>;
  description?: InputMaybe<string>;
  brandId?: InputMaybe<string>;
  itemId?: InputMaybe<string>;
  type?: InputMaybe<MaterialType>;
  variants: InputMaybe<Array<MaterialVariantFormValue>>;
  /** Listing-level, even though the design's "Set as Default" checkbox sits in the variant section. */
  defaultVariantId?: InputMaybe<string>;
  optionPricingConfig: OptionPricingFormValue;
  materialLeadTimeConfig: MaterialLeadTimeFormValue;
  designNote?: InputMaybe<string>;
  fieldProposals?: InputMaybe<Array<AiFieldProposalFormValue>>;
};

export type MaterialListingFormState = ObjectState<MaterialListingFormValue>;

export const materialListingFormConfig: (props: {
  canEditItem: boolean;
  canEditType: boolean;
}) => ObjectConfig<MaterialListingFormValue> = ({ canEditItem, canEditType }) => ({
  id: { type: "value" },
  type: { type: "value", rules: [required], readOnly: !canEditType },
  itemId: { type: "value", rules: [required], readOnly: !canEditItem },
  brandId: { type: "value" },
  name: { type: "value", rules: [required] },
  description: { type: "value" },
  fieldProposals: { type: "list", update: "incremental", config: aiFieldProposalConfig },
  defaultVariantId: { type: "value" },
  variants: {
    type: "list",
    update: "incremental",
    config: {
      id: { type: "value" },
      code: { type: "value" },
      canEdit: { type: "value" },
      codeOverride: { type: "value" },
      modelNumber: { type: "value" },
      manufacturerUrl: { type: "value" },
      designFeedback: { type: "value" },
      deletedAt: { type: "value" },
      mavIds: { type: "value" },
      priceEstimate: { type: "value" },
      tags: { type: "value" },
      fieldProposals: { type: "list", update: "incremental", config: aiFieldProposalConfig },
      images: {
        type: "list",
        config: {
          id: { type: "value" },
          sortOrder: { type: "value" },
          attachmentUrl: { type: "value" },
          downloadUrl: { type: "value" },
          // Server-owned; the save input has no such field.
          sourceUrl: { type: "value", isLocalOnly: true },
          asset: {
            type: "object",
            config: {
              id: { type: "value" },
              s3Key: { type: "value" },
              fileName: { type: "value" },
              contentType: { type: "value" },
              sizeInBytes: { type: "value" },
              delete: { type: "value" },
            },
          },
        },
      },
      components: {
        type: "list",
        config: {
          id: { type: "value" },
          // Stable React key for rows that don't have a server id yet; excluded from changedValue.
          clientKey: { type: "value", isLocalOnly: true },
          assemblyId: { type: "value" },
          componentId: { type: "value" },
          componentItemId: { type: "value" },
          componentValues: { type: "value" },
          qualifier: { type: "value" },
        },
      },
    },
  },
  optionPricingConfig: { type: "object", config: optionPricingConfigConfig },
  materialLeadTimeConfig: { type: "object", config: materialLeadTimeConfigConfig },
  designNote: { type: "value" },
});

export function getPriceEstimateText(
  variant: Pick<MaterialVariant, "minCostInMills" | "maxCostInMills">,
  fallback: string,
) {
  const { minCostInMills, maxCostInMills } = variant;

  if (!isDefined(minCostInMills) && !isDefined(maxCostInMills)) return fallback;

  // 1 mill = 0.1 cents, we divide by 10 to get cents
  const formattedMin = formatToPrice({ valueInCents: (minCostInMills ?? 0) / 10, maxDecimalPlaces: 0 });
  const formattedMax = formatToPrice({ valueInCents: (maxCostInMills ?? 0) / 10, maxDecimalPlaces: 0 });
  if (minCostInMills === maxCostInMills) return formattedMin;

  return `${formattedMin} - ${formattedMax}`;
}

/**
 * Builds the GraphQL save input for a material variant.
 *
 * Scalars come from `changed` (`changedValue`). Images/components come from `full` (`value`) so a
 * sparse list does not drop unchanged siblings.
 */
export function saveMaterialListingVariantInput(
  changed: MaterialVariantFormValue,
  full: MaterialVariantFormValue = changed,
): SaveMaterialListingVariantInput {
  // Only send a collection when it is in `changed` (i.e. the user touched it), but send it exhaustively
  // from `full` — `changed` rows can be field-sparse and, after a dirty refresh, may omit a just-saved
  // row that never got its server id attached.
  const images = changed.images !== undefined ? (full.images ?? changed.images) : undefined;
  const components = changed.components !== undefined ? (full.components ?? changed.components) : undefined;
  return {
    ...omit(changed, ["canEdit", "code", "components", "images", "priceEstimate", "fieldProposals"]),
    images: images?.map((image) => omit(image, "downloadUrl", "attachmentUrl", "sourceUrl")),
    components: components?.map((component) => {
      const { componentId, componentItemId, componentValues, clientKey: _clientKey, ...otherOpts } = component;
      // Prefer a stable MaterialVariant reference when we have one. Only use the item+values path for
      // true "New [item]" selections (Item-tagged componentId) so we don't re-resolve placeholders on
      // every save of an existing MAC (existing rows carry both componentId=mv: and componentItemId=i:).
      if (componentId && maybeTagFromId(componentId) === "mv") {
        return { ...otherOpts, componentId };
      }
      const itemId = componentItemId ?? (componentId && maybeTagFromId(componentId) === "i" ? componentId : undefined);
      // With an item id, let the backend create/find the placeholder from the item + values;
      // otherwise reference the selected MaterialVariant directly.
      return itemId ? { ...otherOpts, componentItemId: itemId, componentValues } : { ...otherOpts, componentId };
    }),
  };
}

/**
 * Builds the save input's `proposals` sidecar: the listing's own resolved AI proposals, plus every
 * variant's (variant proposals have no per-variant input, so they ride on the listing's one sidecar).
 *
 * `fieldProposals` is form-only bookkeeping — `SaveMaterialListingInput` takes `proposals` instead —
 * so callers must also split it out of `changedValue` rather than spreading it into the input.
 */
export function saveMaterialListingProposalsInput(formState: MaterialListingFormState): ResolveAiFieldProposalInput[] {
  return [
    ...resolvedProposalsInput(formState.fieldProposals.changedValue),
    ...formState.variants.rows.flatMap((variant) => resolvedProposalsInput(variant.fieldProposals.changedValue)),
  ];
}

/**
 * The rows the user (or the Create sweep) actually resolved.
 */
function resolvedProposalsInput(rows: MaterialListingFormValue["fieldProposals"]): ResolveAiFieldProposalInput[] {
  return (rows ?? []).flatMap((row) =>
    row.id && row.status && row.status !== AiFieldProposalStatus.Pending ? [{ id: row.id, status: row.status }] : [],
  );
}
